import type { ComponentPropsWithoutRef, MediaHTMLAttributes } from 'react';
import NextImage from 'next/image';

type VideoSrc = MediaHTMLAttributes<unknown>['src'];
type NextImageSrc = ComponentPropsWithoutRef<typeof NextImage>['src'];
type Src = NextImageSrc | VideoSrc;

function isContentfulUrl(src: string): boolean {
  return (
    isContentfulAssetUrl(src) ||
    isContentfulDownloadUrl(src) ||
    isContentfulImageUrl(src) ||
    isContentfulVideoUrl(src)
  );
}

export function isContentfulAssetUrl(src: string): boolean {
  return (
    src.startsWith('http://assets.ctfassets.net/') ||
    src.startsWith('https://assets.ctfassets.net/')
  );
}

export function isContentfulDownloadUrl(src: string): boolean {
  return (
    src.startsWith('http://downloads.ctfassets.net/') ||
    src.startsWith('https://downloads.ctfassets.net/')
  );
}

export function isContentfulImageUrl(src: string): boolean {
  return (
    src.startsWith('http://images.ctfassets.net/') ||
    src.startsWith('https://images.ctfassets.net/')
  );
}

export function isContentfulAssetProxyUrl(src: string): boolean {
  return (
    src.startsWith('https://assets.meinschiff.com') ||
    src.startsWith('https://assets.aws.test.meinschiff.com')
  );
}

export function isContentfulVideoUrl(src: string): boolean {
  return (
    src.startsWith('http://videos.ctfassets.net/') ||
    src.startsWith('https://videos.ctfassets.net/')
  );
}

const DOMAIN_MAP: Record<string, string> = {
  'images.ctfassets.net': 'images',
  'assets.ctfassets.net': 'assets',
  'videos.ctfassets.net': 'videos',
  'downloads.ctfassets.net': 'downloads',
};

export function applyContentfulCdnProxyIfEnabled(src: string): string;
export function applyContentfulCdnProxyIfEnabled(src: undefined): undefined;
export function applyContentfulCdnProxyIfEnabled(
  src: string | undefined,
): string | undefined;
export function applyContentfulCdnProxyIfEnabled(
  src: NextImageSrc,
): NextImageSrc;
export function applyContentfulCdnProxyIfEnabled(src: VideoSrc): VideoSrc;
export function applyContentfulCdnProxyIfEnabled(
  src: Src | undefined,
): Src | undefined {
  if (typeof src !== 'string') return src;
  if (process.env.NEXT_PUBLIC_CDN_PROXY !== 'true') return src;
  if (!isContentfulUrl(src)) return src;
  if (isContentfulAssetProxyUrl(src)) return src;

  let url = new URL(src);
  const prefix = DOMAIN_MAP[url.hostname];

  url.hostname = 'assets.meinschiff.com';
  url.pathname = `/${prefix}${url.pathname}`; // Add prefix based on content type

  return url.href;
}
